import { normalizeMenuPath } from '@/utils/menu'

// ============================================================
// 首页看板：菜单业务分组（严格基于接口菜单树目录层级） + 分组配色 + 常用功能本地配置
// ------------------------------------------------------------
// 分组规则：以接口返回的「顶层节点」为一个分组
//   - 顶层目录（authType=0 且无 authUrl）：其下所有可点击叶子作为组内卡片
//   - 顶层叶子（有 authUrl）：自身单独成一个分组（如"我的报告"）
// 分组标题 = 顶层节点的 authName（接口目录名，后端配置为准）
// 分组配色 tone = 按分组标题关键词做语义匹配，与项目主题色协调、降饱和
// ============================================================

// 首页本身不计入分组
const EXCLUDE_PATHS = new Set(['/dashboard'])

/**
 * 根据分组标题关键词判定色系 tone
 * 取自项目主题变量体系，与主题蓝紫主色协调
 * @param {string} title 分组标题
 * @returns {string} blue | pink | green | neutral
 */
export function resolveTone (title = '') {
  const t = String(title || '')
  // 用户权限类：账号/组织/角色/权限维护 → 蓝色系（主题主色）
  if (/(用户|权限|账号|部门|角色|组织|人员|员工|成员|中心)/.test(t)) return 'blue'
  // 报告业务类：报告/模板/审核/行程 → 粉色系（紫→粉，呼应主题 accent）
  if (/(报告|模板|审核|纷享|申请|行程|公文|文档)/.test(t)) return 'pink'
  // 系统配置类：系统/日志/通知/反馈 → 浅绿系（主题 success）
  if (/(系统|日志|通知|异常|统计|反馈|菜单|配置|设置|公告|字典|参数)/.test(t)) return 'green'
  return 'neutral'
}

/**
 * 递归收集某顶层节点下所有可点击叶子（有 authUrl 且非 /dashboard）
 * 给每个叶子打上所属分组标记（groupKey/groupTitle/tone/parentName）
 */
function collectLeafChildren (children, parentName, groupKey, groupTitle, tone, out) {
  ;(children || []).forEach(child => {
    if (!child) return
    const path = normalizeMenuPath(child.authUrl)
    if (path && !EXCLUDE_PATHS.has(path)) {
      out.push({ ...child, parentName, groupKey, groupTitle, tone })
    }
    if (child.children && child.children.length) {
      collectLeafChildren(child.children, parentName, groupKey, groupTitle, tone, out)
    }
  })
}

/**
 * 严格基于接口菜单树目录层级生成分组
 * @param {Array} menuTree 经过 buildMenuTree 处理后的菜单树
 * @returns {Array<{ key:string, title:string, tone:string, menus:Array }>}
 *          每个菜单项额外带 groupKey/groupTitle/tone/parentName 字段
 */
export function buildEntryGroups (menuTree = []) {
  const groups = []
  ;(menuTree || []).forEach(node => {
    if (!node) return
    const path = normalizeMenuPath(node.authUrl)
    // 首页本身排除
    if (EXCLUDE_PATHS.has(path)) return

    const groupKey = 'g-' + (node.authId !== undefined ? node.authId : (node.authName || Math.random()))
    const title = node.authName || '功能'
    const tone = resolveTone(title)
    const menus = []

    if (path) {
      // 顶层叶子（有 authUrl）：自身单独成一个分组
      menus.push({ ...node, parentName: '', groupKey, groupTitle: title, tone })
    } else {
      // 顶层目录（无 authUrl）：收集其下所有可点击叶子
      collectLeafChildren(node.children, node.authName, groupKey, title, tone, menus)
    }

    if (menus.length) {
      groups.push({ key: groupKey, title, tone, menus })
    }
  })
  return groups
}

/**
 * 把分组数组拍平为全部可点击菜单列表（用于常用功能查找映射）
 * @param {Array} groups buildEntryGroups 的返回值
 * @returns {Array} 拍平的菜单列表，每项带 groupKey/groupTitle/tone
 */
export function flattenGroupMenus (groups = []) {
  const result = []
  ;(groups || []).forEach(g => {
    ;(g.menus || []).forEach(m => result.push(m))
  })
  return result
}

/**
 * 将一批菜单按其所属分组重新归类（用于常用功能按分组展示）
 * 保持分组在原 entryGroups 中的顺序
 * @param {Array} menus 菜单项（需带 groupKey/groupTitle/tone）
 * @param {Array} orderGroups 分组顺序参考（entryGroups）
 * @returns {Array<{ key:string, title:string, tone:string, menus:Array }>}
 */
export function regroupMenus (menus = [], orderGroups = []) {
  const orderMap = new Map()
  ;(orderGroups || []).forEach((g, i) => orderMap.set(g.key, i))

  const bucketMap = new Map()
  ;(menus || []).forEach(m => {
    const gk = m && m.groupKey
    if (!gk) return
    if (!bucketMap.has(gk)) {
      bucketMap.set(gk, { key: gk, title: m.groupTitle, tone: m.tone, menus: [] })
    }
    bucketMap.get(gk).menus.push(m)
  })

  return Array.from(bucketMap.values()).sort((a, b) => {
    const oa = orderMap.has(a.key) ? orderMap.get(a.key) : 9999
    const ob = orderMap.has(b.key) ? orderMap.get(b.key) : 9999
    return oa - ob
  })
}

// ============ 常用功能本地配置（localStorage，按账号隔离） ============

const FAVORITE_PREFIX = 'report_management_dashboard_favorites_'

function favoriteKey (userId) {
  const id = String(userId || 'default').trim() || 'default'
  return FAVORITE_PREFIX + id
}

/**
 * 读取当前账号保存的常用 authId 列表
 * 读出时会自动过滤掉当前菜单中已不存在的 authId（保留原始类型，避免 checkbox 类型不匹配）
 * @param {string} userId 用户标识（auName/userName）
 * @param {Array} availableMenus 当前可用菜单（用于过滤失效项），不传则不过滤
 * @returns {Array} authId 有序数组
 */
export function getFavoriteIds (userId, availableMenus = null) {
  const raw = localStorage.getItem(favoriteKey(userId))
  if (!raw) return []
  let ids = []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) ids = parsed.filter(x => x !== null && x !== undefined && x !== '')
  } catch (e) {
    ids = []
  }
  // 过滤当前菜单中已不存在的 authId（保持原值类型）
  if (Array.isArray(availableMenus) && availableMenus.length) {
    const validSet = new Set(availableMenus.map(m => m.authId))
    ids = ids.filter(id => validSet.has(id))
  }
  return ids
}

/**
 * 保存当前账号的常用 authId 列表（去重、保序，保留原值类型）
 * @param {string} userId 用户标识
 * @param {Array} ids authId 有序数组
 */
export function setFavoriteIds (userId, ids = []) {
  const seen = new Set()
  const deduped = []
  ;(ids || []).forEach(id => {
    if (id === null || id === undefined || id === '') return
    if (seen.has(id)) return
    seen.add(id)
    deduped.push(id)
  })
  localStorage.setItem(favoriteKey(userId), JSON.stringify(deduped))
  return deduped
}
